/** Browser-safe P3 secure collaboration contracts. */
export const SECURE_CONTROL_STATELESS_PREFIX_V1 = "playbookai:collab:secure-control:v1:";
export const SECURE_AUTHORIZATION_CLOSE_REASON_PREFIX_V1 = "collab-authz-v1";
export function decodeSecureTicketOutcomeV1(value) {
    try {
        const input = record(value);
        switch (input["kind"]) {
            case "issued":
                exactKeys(input, ["kind", "ticket", "expiresAt"]);
                return {
                    ok: true,
                    outcome: Object.freeze({
                        kind: "issued",
                        ticket: requireString(input["ticket"]),
                        expiresAt: requirePositiveSafeInteger(input["expiresAt"]),
                    }),
                };
            case "retryable": {
                const hasRetryAfter = Object.prototype.hasOwnProperty.call(input, "retryAfterMs");
                exactKeys(input, hasRetryAfter
                    ? ["kind", "reason", "retryAfterMs"]
                    : ["kind", "reason"]);
                return {
                    ok: true,
                    outcome: Object.freeze({
                        kind: "retryable",
                        reason: requireString(input["reason"]),
                        ...(hasRetryAfter
                            ? {
                                retryAfterMs: requirePositiveSafeInteger(input["retryAfterMs"]),
                            }
                            : {}),
                    }),
                };
            }
            case "terminal":
                exactKeys(input, ["kind", "reason"]);
                return {
                    ok: true,
                    outcome: Object.freeze({
                        kind: "terminal",
                        reason: requireString(input["reason"]),
                    }),
                };
            default:
                throw new Error("kind");
        }
    }
    catch {
        return { ok: false, reason: "invalid_ticket_outcome" };
    }
}
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const UINT64_DECIMAL = /^(?:0|[1-9][0-9]{0,19})$/;
const NONZERO_UINT64_DECIMAL = /^[1-9][0-9]{0,19}$/;
const UINT64_MAX = 18446744073709551615n;
export function secureDocumentIdentityV1(input) {
    if (!CANONICAL_UUID.test(input.tenantId) ||
        !CANONICAL_UUID.test(input.entityId) ||
        (input.docType !== "solve_canvas" &&
            input.docType !== "hoshin_canvas" &&
            input.docType !== "vsm_canvas")) {
        throw new Error("invalid_document_identity");
    }
    return Object.freeze({
        tenantId: input.tenantId,
        docType: input.docType,
        entityId: input.entityId,
    });
}
export function formatSecureDocumentNameV1(identity) {
    const checked = secureDocumentIdentityV1(identity);
    return `${checked.tenantId}:${checked.docType}:${checked.entityId}`;
}
export function assertSecureProviderSetupV1(setup) {
    secureDocumentIdentityV1(setup.document);
    if (setup.protocolMode === "secure" && setup.broadcast !== false) {
        throw new Error("secure_provider_broadcast_must_be_false");
    }
    if (setup.mode === "edit" || setup.mode === "participate") {
        requireUint32(setup.collabClientId);
    }
    else if (setup.collabClientId !== undefined) {
        throw new Error("observe_collab_client_id_forbidden");
    }
}
export function normalizeRetryAfterMsV1(retryAfter) {
    if (retryAfter === undefined || retryAfter === null)
        return undefined;
    if (!/^[1-9][0-9]*$/.test(retryAfter))
        return undefined;
    const seconds = Number(retryAfter);
    if (!Number.isSafeInteger(seconds))
        return undefined;
    const milliseconds = seconds * 1_000;
    return Number.isSafeInteger(milliseconds) && milliseconds > 0
        ? milliseconds
        : undefined;
}
export function decodeSecureControlStatelessV1(payload) {
    if (!payload.startsWith(SECURE_CONTROL_STATELESS_PREFIX_V1)) {
        return { kind: "not_control" };
    }
    const suffix = payload.slice(SECURE_CONTROL_STATELESS_PREFIX_V1.length);
    if (suffix.length === 0)
        return terminal();
    try {
        const raw = JSON.parse(suffix);
        const control = parseControl(raw);
        if (JSON.stringify(control) !== suffix)
            return terminal();
        return { kind: "control", control };
    }
    catch {
        return terminal();
    }
}
function terminal() {
    return { kind: "terminal", reason: "binding_protocol_violation" };
}
function parseControl(value) {
    const input = record(value);
    if ((input["version"] !== 1 && input["version"] !== 2) ||
        typeof input["type"] !== "string") {
        throw new Error("header");
    }
    if (input["version"] === 2 &&
        input["type"] !== "presence_snapshot" &&
        input["type"] !== "presence_upsert") {
        throw new Error("version");
    }
    const domain = parseDomain(input["domain"]);
    if (input["version"] === 2 && domain.document.docType !== "vsm_canvas") {
        throw new Error("version domain");
    }
    switch (input["type"]) {
        case "binding_snapshot":
            exactKeys(input, ["version", "type", "domain", "binding"]);
            return Object.freeze({
                version: 1,
                type: "binding_snapshot",
                domain,
                binding: parseBinding(input["binding"]),
            });
        case "presence_snapshot":
            exactKeys(input, ["version", "type", "domain", "entries"]);
            if (!Array.isArray(input["entries"]))
                throw new Error("entries");
            return Object.freeze({
                version: input["version"],
                type: "presence_snapshot",
                domain,
                entries: Object.freeze(input["entries"].map(parsePresenceEntry)),
            });
        case "binding_upsert": {
            exactKeys(input, ["version", "type", "domain", "binding"]);
            const binding = parseBinding(input["binding"]);
            if (binding.state !== "bound")
                throw new Error("binding");
            return Object.freeze({
                version: 1,
                type: "binding_upsert",
                domain,
                binding,
            });
        }
        case "presence_upsert":
            exactKeys(input, ["version", "type", "domain", "entry"]);
            return Object.freeze({
                version: input["version"],
                type: "presence_upsert",
                domain,
                entry: parsePresenceEntry(input["entry"]),
            });
        case "binding_tombstone":
        case "presence_remove":
            exactKeys(input, [
                "version",
                "type",
                "domain",
                "clientId",
                "bindingGeneration",
            ]);
            return Object.freeze({
                version: 1,
                type: input["type"],
                domain,
                clientId: requireUint32(input["clientId"]),
                bindingGeneration: decimalGeneration(input["bindingGeneration"]),
            });
        default:
            throw new Error("type");
    }
}
function parseDomain(value) {
    const input = record(value);
    exactKeys(input, ["originId", "document", "documentGeneration"]);
    const originId = requireString(input["originId"]);
    if (!CANONICAL_UUID.test(originId))
        throw new Error("originId");
    const documentInput = record(input["document"]);
    exactKeys(documentInput, ["tenantId", "docType", "entityId"]);
    const document = secureDocumentIdentityV1({
        tenantId: requireString(documentInput["tenantId"]),
        docType: requireString(documentInput["docType"]),
        entityId: requireString(documentInput["entityId"]),
    });
    const generation = requireString(input["documentGeneration"]);
    if (!NONZERO_UINT64_DECIMAL.test(generation) ||
        BigInt(generation) > UINT64_MAX) {
        throw new Error("documentGeneration");
    }
    return Object.freeze({
        originId: originId,
        document,
        documentGeneration: generation,
    });
}
function parseBinding(value) {
    const input = record(value);
    if (input["state"] === "unbound") {
        exactKeys(input, ["state"]);
        return Object.freeze({ state: "unbound" });
    }
    if (input["state"] === "tombstoned") {
        exactKeys(input, ["state", "clientId", "bindingGeneration"]);
        return Object.freeze({
            state: "tombstoned",
            clientId: requireUint32(input["clientId"]),
            bindingGeneration: decimalGeneration(input["bindingGeneration"]),
        });
    }
    if (input["state"] !== "bound")
        throw new Error("state");
    exactKeys(input, [
        "state",
        "connectionKey",
        "connectionGeneration",
        "clientId",
        "bindingGeneration",
        "userId",
        "resourceAuthorizationGeneration",
        "baseRole",
        "isFacilitator",
        "lastAwarenessClock",
    ]);
    const baseRole = input["baseRole"];
    if (baseRole !== "owner" &&
        baseRole !== "collaborator" &&
        baseRole !== "viewer") {
        throw new Error("baseRole");
    }
    const lastClock = input["lastAwarenessClock"];
    if (lastClock !== null)
        requireClock(lastClock);
    return Object.freeze({
        state: "bound",
        connectionKey: requireString(input["connectionKey"]),
        connectionGeneration: decimalGeneration(input["connectionGeneration"]),
        clientId: requireUint32(input["clientId"]),
        bindingGeneration: decimalGeneration(input["bindingGeneration"]),
        userId: requireString(input["userId"]),
        resourceAuthorizationGeneration: decimalGeneration(input["resourceAuthorizationGeneration"]),
        baseRole,
        isFacilitator: requireBoolean(input["isFacilitator"]),
        lastAwarenessClock: lastClock,
    });
}
function parsePresenceEntry(value) {
    const input = record(value);
    exactKeys(input, ["clientId", "bindingGeneration", "clock", "value"]);
    const presence = input["value"];
    if (presence !== null) {
        record(presence);
        requireJsonValue(presence);
    }
    return Object.freeze({
        clientId: requireUint32(input["clientId"]),
        bindingGeneration: decimalGeneration(input["bindingGeneration"]),
        clock: requireClock(input["clock"]),
        value: presence,
    });
}
function decimalGeneration(value) {
    const text = requireString(value);
    if (!UINT64_DECIMAL.test(text) || BigInt(text) > UINT64_MAX) {
        throw new Error("generation");
    }
    return text;
}
function record(value) {
    if (typeof value !== "object" ||
        value === null ||
        Array.isArray(value) ||
        Object.getPrototypeOf(value) !== Object.prototype) {
        throw new Error("object");
    }
    return value;
}
function exactKeys(value, expected) {
    const keys = Object.keys(value).sort();
    const wanted = [...expected].sort();
    if (keys.length !== wanted.length ||
        keys.some((key, i) => key !== wanted[i])) {
        throw new Error("keys");
    }
}
function requireString(value) {
    if (typeof value !== "string" || value.length === 0)
        throw new Error("string");
    return value;
}
function requireBoolean(value) {
    if (typeof value !== "boolean")
        throw new Error("boolean");
    return value;
}
function requireUint32(value) {
    if (typeof value !== "number" ||
        !Number.isInteger(value) ||
        value < 0 ||
        value > 0xffff_ffff) {
        throw new Error("uint32");
    }
    return value;
}
function requirePositiveSafeInteger(value) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
        throw new Error("positive safe integer");
    }
    return value;
}
function requireClock(value) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
        throw new Error("clock");
    }
    return value;
}
function requireJsonValue(value) {
    if (value === null ||
        typeof value === "string" ||
        typeof value === "boolean") {
        return;
    }
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new Error("number");
        return;
    }
    if (Array.isArray(value)) {
        for (const entry of value)
            requireJsonValue(entry);
        return;
    }
    const input = record(value);
    for (const entry of Object.values(input))
        requireJsonValue(entry);
}
//# sourceMappingURL=contracts.js.map